import React, { useState } from "react";
import { BadgeCheck, ShieldCheck, ImageOff } from "lucide-react";
import { photoUrl } from "../lib/api";

export function PhotoImg({ photoId, alt, className, fallbackInitial }) {
  const [err, setErr] = useState(false);
  const url = photoId ? photoUrl(photoId) : null;
  if (!url || err) {
    return (
      <div className={`flex items-center justify-center bg-gradient-to-br from-[#1C171A] to-[#541224]/40 text-[#D4AF37] ${className || ""}`}>
        {fallbackInitial ? <span className="font-serif text-3xl">{fallbackInitial}</span> : <ImageOff size={28} className="opacity-50" />}
      </div>
    );
  }
  return <img src={url} alt={alt || "photo"} onError={() => setErr(true)} className={className} loading="lazy" />;
}

export function VerifiedBadge({ selfie, id, size = "sm" }) {
  if (!selfie && !id) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] font-medium ${size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}>
      {id ? <ShieldCheck size={13} /> : <BadgeCheck size={13} />}
      {id ? "ID Verified" : "Selfie Verified"}
    </span>
  );
}

export function OfferChip({ amount, label = "Preferred Offer" }) {
  return (
    <div className="inline-flex flex-col items-start rounded-xl border border-[#D4AF37]/30 bg-[#541224]/50 px-3 py-1.5">
      <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A96E]">{label}</span>
      <span className="font-serif text-xl text-[#FBF8F3] leading-none">${amount ?? "—"}</span>
    </div>
  );
}

const STATUS_STYLES = {
  PENDING: "bg-[#C89528]/20 text-[#E0B84A] border-[#C89528]/40",
  COUNTERED: "bg-[#8554B8]/20 text-[#C6A6F0] border-[#8554B8]/40",
  ACCEPTED: "bg-[#2E7D52]/20 text-[#6FD79E] border-[#2E7D52]/40",
  DECLINED: "bg-[#8B263E]/20 text-[#E58CA0] border-[#8B263E]/40",
  EXPIRED: "bg-white/5 text-[#9E978E] border-white/10",
  CANCELLED: "bg-white/5 text-[#9E978E] border-white/10",
  DATE_PLANNING: "bg-[#2E7D52]/15 text-[#6FD79E] border-[#2E7D52]/40",
  DATE_CONFIRMED: "bg-[#2E7D52]/25 text-[#8FE7B4] border-[#2E7D52]/50",
  COMPLETED: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  DISPUTED: "bg-[#8B263E]/25 text-[#E58CA0] border-[#8B263E]/50",
};

export function StatusTag({ status }) {
  const cls = STATUS_STYLES[status] || "bg-white/5 text-[#9E978E] border-white/10";
  const label = (status || "").replace(/_/g, " ");
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${cls}`}>{label}</span>;
}

export function GoldButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`db-offer-btn rounded-full px-5 py-2.5 text-sm font-semibold tracking-wide disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`rounded-full px-5 py-2.5 text-sm font-medium border border-[#D4AF37]/25 text-[#D8D2C9] hover:border-[#D4AF37]/60 hover:text-[#FBF8F3] transition-colors disabled:opacity-50 ${className}`}>
      {children}
    </button>
  );
}

export function SectionTitle({ eyebrow, title, className = "" }) {
  return (
    <div className={className}>
      {eyebrow && <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">{eyebrow}</div>}
      <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF8F3] tracking-tight">{title}</h2>
    </div>
  );
}
